import React from "react";
import { CATEGORY_STYLES } from "@/components/metrics/styles";
import { formatDate } from "@/lib/date-formatting";
import {
  METRIC_CONFIG,
  renderMetric,
  type SalespersonMetric,
} from "@/pages/salespeople/salesperson-explorer/salesperson-metrics-config";
import type { SalespersonMetrics } from "@/types/salesperson-types";

interface CompactMetricCardProps {
  metric: SalespersonMetric;
  metrics?: SalespersonMetrics;
  loadingStates?: Record<
    SalespersonMetric,
    { isLoading: boolean; isError: boolean }
  >;
}

export function CompactMetricCard({
  metric,
  metrics,
  loadingStates,
}: CompactMetricCardProps) {
  const config = METRIC_CONFIG[metric];
  const categoryStyle = CATEGORY_STYLES[config.category];
  const metricValue = metrics?.[metric];

  // Fix: Handle 0 properly - 0 is a valid numeric value, not null
  const rawValue = typeof metricValue === "number" ? metricValue : null;
  const isLoading = loadingStates?.[metric]?.isLoading || false;

  // Check if this metric would have secondary content based on comprehensive patterns
  const hasSecondaryContent = (() => {
    // Special case: For accounts_engaged, people_engaged, and deal_contacts_engaged, if value is 0, treat as simple metric
    if (
      (metric === "accounts_engaged" ||
        metric === "people_engaged" ||
        metric === "deal_contacts_engaged") &&
      rawValue === 0
    ) {
      return false;
    }

    // Amount metrics themselves should be treated as simple metrics in drilldown cards
    // They don't have secondary content - they ARE the secondary content for count metrics
    if (metric.includes("amount")) {
      return false;
    }

    // Metrics with per-entity secondary values (always show these, unless value is 0 for specific metrics)
    if (
      metric === "accounts_engaged" ||
      metric === "people_engaged" ||
      metric === "deal_contacts_engaged" ||
      metric.includes("per_")
    ) {
      return true;
    }

    // Metrics with associated amount values - only show secondary content if amount > 0
    // Per user requirement: when amount = 0, treat as if there is no subheading
    const amountMetrics: Record<string, keyof SalespersonMetrics> = {
      opportunities_influenced: "opportunities_influenced_amount",
      opportunities_influenced_won: "opportunities_influenced_won_amount",
      opportunities_influenced_in_cycle:
        "opportunities_influenced_in_cycle_amount",
      opportunities_created_post_engagement:
        "opportunities_created_post_engagement_amount",
    };

    const amountMetricKey = amountMetrics[metric];
    if (amountMetricKey && metrics) {
      const amountValue = metrics[amountMetricKey];
      // Only show secondary content for positive amounts (0 = no subheading per requirements)
      return typeof amountValue === "number" && amountValue > 0;
    }

    return false;
  })();

  // For simple metrics, render just the formatted primary value
  let displayValue: React.ReactNode;

  if (!hasSecondaryContent) {
    // Format the primary value directly without the flex column structure
    // Fix: Only show "—" for null/undefined, not for 0
    if (rawValue === null || rawValue === undefined) {
      // Fallback: Use the full render function but extract just the primary value
      const fullRender = config.render
        ? config.render(rawValue, {
            metricKey: metric,
            metrics,
            isBenchmark: false,
          })
        : renderMetric(rawValue, {
            metricKey: metric,
            metrics,
            isBenchmark: false,
          });

      // Extract just the primary value from the flex column structure
      if (React.isValidElement(fullRender) && fullRender.props.children) {
        const children = fullRender.props.children;
        // Get the first child (primary value) from the flex column
        displayValue = Array.isArray(children) ? children[0] : fullRender;
      } else {
        displayValue = fullRender;
      }
    } else {
      const isCurrency = metric.includes("amount");
      if (isCurrency) {
        displayValue = new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(rawValue);
      } else if (metric.includes("date")) {
        displayValue = rawValue ? formatDate(String(rawValue)) : "—";
      } else {
        // This should show "0" for zero values, not "—"
        displayValue = new Intl.NumberFormat("en-US").format(rawValue);
      }
    }
  } else {
    // For compact metric cards, format values directly with appropriate font sizes
    // rather than using the full render function which has large responsive fonts
    const isCurrency = metric.includes("amount");
    const primaryFormatted =
      rawValue === null || rawValue === undefined
        ? "—"
        : isCurrency
          ? new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(rawValue)
          : new Intl.NumberFormat("en-US").format(rawValue);

    // Get secondary value if needed
    let secondaryContent: React.ReactNode = null;

    // Check for amount metrics
    const amountMetrics: Record<string, keyof SalespersonMetrics> = {
      opportunities_influenced: "opportunities_influenced_amount",
      opportunities_influenced_won: "opportunities_influenced_won_amount",
      opportunities_influenced_in_cycle:
        "opportunities_influenced_in_cycle_amount",
      opportunities_created_post_engagement:
        "opportunities_created_post_engagement_amount",
    };

    const amountMetricKey = amountMetrics[metric];
    if (amountMetricKey && metrics && rawValue !== 0) {
      const amountValue = metrics[amountMetricKey];
      if (typeof amountValue === "number") {
        const formattedAmount = new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(amountValue);
        secondaryContent = <span>{formattedAmount}</span>;
      }
    }

    // Check for per-entity metrics
    if (
      (metric === "accounts_engaged" ||
        metric === "people_engaged" ||
        metric === "deal_contacts_engaged") &&
      metrics &&
      rawValue !== 0
    ) {
      const perEntityMetrics: Record<string, keyof SalespersonMetrics> = {
        accounts_engaged: "engagements_per_account",
        people_engaged: "engagements_per_person",
        deal_contacts_engaged: "engagements_per_deal_contact",
      };
      const perEntityKey = perEntityMetrics[metric];
      if (perEntityKey) {
        const perEntityValue = metrics[perEntityKey];
        if (typeof perEntityValue === "number") {
          const formattedValue = perEntityValue.toFixed(1);
          const label =
            metric === "accounts_engaged"
              ? perEntityValue === 1
                ? "engagement/account"
                : "engagements/account"
              : metric === "people_engaged"
                ? perEntityValue === 1
                  ? "engagement/person"
                  : "engagements/person"
                : perEntityValue === 1
                  ? "engagement/contact"
                  : "engagements/contact";

          secondaryContent = (
            <span className="inline-flex items-center gap-1">
              <span>{formattedValue}</span>
              <span className="font-normal whitespace-nowrap">{label}</span>
            </span>
          );
        }
      }
    }

    displayValue = (
      <div className="flex flex-col">
        <div className="text-lg font-semibold">{primaryFormatted}</div>
        <div className="min-h-[20px] text-sm text-muted-foreground/50">
          {secondaryContent}
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-3 px-4 py-2 bg-muted/30 rounded-lg border shadow-sm animate-pulse">
        <div className="w-8 h-8 bg-muted rounded flex items-center justify-center">
          <div className="w-4 h-4 bg-muted-foreground/20 rounded"></div>
        </div>
        <div className="flex-1">
          <div className="h-6 bg-muted-foreground/20 rounded w-16"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 px-4 py-2 bg-card rounded-lg border shadow-sm">
      {/* Category Icon */}
      <div
        className={`flex items-center justify-center w-8 h-8 rounded ${categoryStyle.color}`}
      >
        {categoryStyle.icon}
      </div>

      {/* Metric Content */}
      <div className="flex-1 min-w-0">
        {!hasSecondaryContent ? (
          <div className="flex items-center text-lg font-semibold">
            {displayValue}
          </div>
        ) : (
          displayValue
        )}
      </div>
    </div>
  );
}
